import {Dayjs} from "dayjs";
import {PlanRating} from "@bounce/shared/enums";
import {FirstPlan, ICustomizedPaymentPlanDetails, LetterTemplateKey, MatrixPlanType} from "@bounce/shared/consumer/declarations";
import {CommunicationForms, IAccountFlexUIState, IFlexUIState} from "./types";

const FLEX_STATE_KEY = "flex-ui-state";

const getCurrPathname = () => {
  const url = window.location.href;
  return new URL(url).href.replace(new URL(url).origin, "");
};

export const initialForms: CommunicationForms = {
  message: {phone: "", message: "", date: null as unknown as Dayjs},
  email: {email: "", date: null as unknown as Dayjs, subject: "", message: "", files: []},
  phone_call: {phone: "", body: "", date: null as unknown as Dayjs},
  letter: {address: "", description: "", files: [], templateKey: "" as unknown as LetterTemplateKey},
  reminder: {date: null as unknown as Dayjs, note: ""},
};

const getDefaultAccountState = (): IAccountFlexUIState => ({
  timestamp: new Date().toISOString(),
  url: getCurrPathname(),
  payment_plan: {
    restricted_options: false,
    payment_details: {},
    builder: {
      highest_discount: 30,
      plan_matrix: {
        total_amount: 0,
        start_date: new Date().toISOString(),
        installment_size: 0,
        discount_fraction: 0,
        number_of_installments: 0,
        abstract_payment_plan_name: "",
        installment_frequency: "monthly",
        first_plan_details: {} as FirstPlan,
        color: PlanRating.GREEN,
        restricted: false,
        plan_type: MatrixPlanType.MATRIX_NORMAL,
        remainder: 0,
      },
      current_step: 0,
      init_main_date: true,
    },
    partial: {
      amount: 0,
      current_step: 0,
    },
    custom: {
      final_installments: {} as ICustomizedPaymentPlanDetails,
      installments_builder: [],
      discount: 0,
      total_amount: 0,
      current_step: 0,
    },
  },
  arbitrary_communications: {...initialForms, current_dialog: ""},
});

const findMultiKeysPath = (obj: Record<string, any>, key: string): {container: any; path: string[]} | null => {
  const stack = [{current: obj, path: [] as string[]}];

  while (stack.length) {
    const {current, path} = stack.pop()!;

    for (const k in current) {
      if (k === key) return {container: current[k], path: [...path, k]};

      const child = current[k];
      if (child && typeof child === "object" && !Array.isArray(child)) {
        stack.push({current: child, path: [...path, k]});
      }
    }
  }

  return null;
};

const findKeyPath = (obj: any, key: string): {container: any; path: string[]} | null => {
  const keysArray = [{obj, path: [] as string[]}];

  while (keysArray.length) {
    const {obj: cur, path} = keysArray.pop()!;
    if (cur && typeof cur === "object") {
      for (const k in cur) {
        if (k === key) return {container: cur, path};
        keysArray.push({obj: cur[k], path: [...path, k]});
      }
    }
  }

  return null;
};

const getKeysPath = (obj: any, key: string): {container: any; finalKey: string; path: string[]} | null => {
  if (!key) return null;
  const keys = key.split(".");
  if (keys.length === 1) {
    const keyPath = findKeyPath(obj, keys[0]);
    if (keyPath) {
      return {container: keyPath?.container, finalKey: keys[0], path: keyPath.path};
    }
  } else {
    const keysPath = findMultiKeysPath(obj, keys[0]);
    if (keysPath) {
      return {container: keysPath.container, finalKey: keys[1], path: keysPath.path};
    }
  }
  return null;
};

const loadFlexUIState = (): IFlexUIState => {
  const flexState = sessionStorage.getItem(FLEX_STATE_KEY);
  try {
    const jsonFlexState = flexState && JSON.parse(flexState);
    if (jsonFlexState && typeof jsonFlexState === "object") {
      return jsonFlexState;
    }
  } catch {}
  return {};
};

const maskLastFourDigits = (value: string): string => {
  if (value && value.length > 4) {
    return value.slice(0, -4);
  }
  return value;
};

export {getKeysPath, loadFlexUIState, FLEX_STATE_KEY, getDefaultAccountState, getCurrPathname, maskLastFourDigits};
